import type { DateKey } from '@kff/engine';
import type { BasisLine } from './invoicing.js';
import type { PriceListData, QuoteTerms } from './pricing.js';

/**
 * Travel compensation (owner 2026-10-29, thread "Hantera reseersättning per kund"): travel inside Stockholm is in the
 * price; travel outside is invoiced on top, the actual cost (tickets, car, hotel) plus the travel time at half the hourly
 * price. The agreement template says the same (Allmänna villkor 6.1). Each client can deviate: everything included,
 * everything invoiced, another share of the hourly price or its own hourly price. Pure rules shared by the API and the web.
 */

/** Where travel is in the price: inside Stockholm (standard), always, or never */
export type TravelIncluded = 'stockholm' | 'all' | 'none';

export interface TravelRule {
  included: TravelIncluded;
  /** the share of the hourly price the travel time is invoiced at; 0.5 = half (standard), 0 = travel time is free */
  timeShare: number;
  /** kr/h the share is taken of; null = the price list's hourly price */
  hourlyRate: number | null;
  /** what was agreed, in the client's words */
  note: string | null;
}

export const STANDARD_TRAVEL: TravelRule = { included: 'stockholm', timeShare: 0.5, hourlyRate: null, note: null };

/** The price list's hourly price ("Extra inspelnings-, redigerings- eller administrationstid"), 1 500 kr when missing. */
export const hourlyPrice = (pl: Pick<PriceListData, 'addOns'> | null | undefined): number => pl?.addOns.find((a) => a.key === 'hour')?.price ?? 1500;

/** Any object into a rule: unknown keys dropped, numbers checked; blanks fall back to the standard. */
export function cleanTravelRule(raw: unknown): TravelRule {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const included = o.included === 'all' || o.included === 'none' ? o.included : 'stockholm';
  const share = o.timeShare === null || o.timeShare === undefined || o.timeShare === '' ? STANDARD_TRAVEL.timeShare : Number(o.timeShare);
  const rate = o.hourlyRate === null || o.hourlyRate === undefined || o.hourlyRate === '' ? null : Number(o.hourlyRate);
  const note = typeof o.note === 'string' && o.note.trim() ? o.note.trim().slice(0, 500) : null;
  return {
    included,
    timeShare: Number.isFinite(share) ? Math.min(1, Math.max(0, Math.round(share * 100) / 100)) : STANDARD_TRAVEL.timeShare,
    hourlyRate: rate !== null && Number.isFinite(rate) && rate > 0 ? Math.min(20_000, Math.round(rate)) : null,
    note
  };
}

/** The client's saved rule, or the standard when it has none. */
export const travelOf = (saved: Partial<TravelRule> | null | undefined): TravelRule => (saved ? cleanTravelRule(saved) : { ...STANDARD_TRAVEL });

export const isStandardTravel = (r: TravelRule) => r.included === 'stockholm' && r.timeShare === STANDARD_TRAVEL.timeShare && r.hourlyRate === null && !r.note;

/** kr per hour of travel time for this client: the share of its hourly price, whole kronor. */
export const travelTimeRate = (r: TravelRule, listHourly: number) => Math.round((r.hourlyRate ?? listHourly) * r.timeShare);

/** The hourly price the agreement sold (the "hour" option on the quote), else the price list's. */
export const agreementHourly = (terms: Pick<QuoteTerms, 'addOns'> | null | undefined, pl: Pick<PriceListData, 'addOns'> | null | undefined): number =>
  terms?.addOns?.find((a) => a.key === 'hour')?.price ?? hourlyPrice(pl);

/** What the client's agreement in force says about travel (owner 2026-10-29: "räknas ut från det avtalet vi har med kunden"). */
export interface AgreementTravel {
  ref: string;
  /** the agreement's travel terms (the standard when the quote has none) */
  rule: TravelRule;
  /** the agreement's hourly price */
  hourly: number;
}

export interface EffectiveTravel {
  rule: TravelRule;
  /** the hourly price the travel-time share is taken of when the rule has no own one */
  hourly: number;
  /** where the rule comes from: adjusted on the client, the agreement, or the standard (no agreement in the Hub) */
  source: 'client' | 'agreement' | 'standard';
  agreementRef: string | null;
}

/** The rule that applies: the client's adjustment, else the agreement's terms, else the standard. */
export function effectiveTravel(client: Partial<TravelRule> | null | undefined, ag: AgreementTravel | null | undefined, listHourly: number): EffectiveTravel {
  const hourly = ag?.hourly ?? listHourly;
  if (client) return { rule: cleanTravelRule(client), hourly, source: 'client', agreementRef: ag?.ref ?? null };
  if (ag) return { rule: ag.rule, hourly, source: 'agreement', agreementRef: ag.ref };
  return { rule: { ...STANDARD_TRAVEL }, hourly, source: 'standard', agreementRef: null };
}

export const sameTravel = (a: TravelRule, b: TravelRule) => a.included === b.included && a.timeShare === b.timeShare && a.hourlyRate === b.hourlyRate && (a.note ?? null) === (b.note ?? null);

/** Stockholms län's 26 municipalities and the districts people write instead (lower case, no accents folded). */
const STOCKHOLM_PLACES = [
  'stockholm', 'sthlm', 'botkyrka', 'danderyd', 'ekero', 'haninge', 'huddinge', 'jarfalla', 'lidingo', 'nacka', 'norrtalje', 'nykvarn', 'nynashamn',
  'salem', 'sigtuna', 'sollentuna', 'solna', 'sundbyberg', 'sodertalje', 'tyreso', 'taby', 'upplands-bro', 'upplands bro', 'upplands vasby', 'vallentuna',
  'vaxholm', 'varmdo', 'osteraker',
  // districts and towns inside those
  'sodermalm', 'kungsholmen', 'ostermalm', 'vasastan', 'norrmalm', 'gamla stan', 'bromma', 'kista', 'hammarby', 'sjostaden', 'hagastaden', 'hornstull',
  'liljeholmen', 'hagersten', 'skarholmen', 'farsta', 'alvsjo', 'globen', 'johanneshov', 'arsta', 'enskede', 'vallingby', 'spanga', 'frosunda', 'arlanda',
  'marsta', 'akersberga', 'gustavsberg', 'saltsjobaden', 'sickla', 'tumba', 'jakobsberg', 'barkarby', 'tullinge', 'flemingsberg', 'morby', 'jordbro'
];
const fold = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Whether a shoot's place is inside Stockholm (Stockholms län): a known municipality or district, or a postcode from 100 00
 * to 199 99. null when the place is blank. Only a hint: the producer decides whether a trip is invoiced.
 */
export function inStockholm(place: string | null | undefined): boolean | null {
  const p = fold(place ?? '').trim();
  if (!p) return null;
  if (/\b1\d{2}\s?\d{2}\b/.test(p)) return true;
  return STOCKHOLM_PLACES.some((w) => new RegExp(`(^|[^a-z])${w.replace(/[-\s]/g, '[-\\s]?')}([^a-z]|$)`).test(p));
}

/** Whether a trip to this place is invoiced under the rule (a blank place counts as outside, since someone chose to add it). */
export function travelInvoiced(r: TravelRule, place: string | null | undefined): boolean {
  if (r.included === 'all') return false;
  if (r.included === 'none') return true;
  return inStockholm(place) !== true;
}

export interface Trip {
  place: string;
  date?: DateKey | null;
  /** the actual cost excluding VAT: tickets, mileage, hotel, parking */
  cost: number;
  /** hours travelling, all people together */
  hours: number;
  projectId?: string | null;
  shootId?: string | null;
}

const dayText = (d: DateKey | null | undefined) => (d ? ` ${Number(d.slice(8, 10))}/${Number(d.slice(5, 7))}` : '');

/** A trip as invoice-basis lines: the cost at cost price, and the travel time at the client's rate (left out when 0). */
export function travelLines(r: TravelRule, listHourly: number, trip: Trip): BasisLine[] {
  const where = `${trip.place.trim() || 'resa'}${dayText(trip.date)}`;
  const base = { source: 'manual' as const, vat: 25 as const, projectId: trip.projectId ?? null, shootId: trip.shootId ?? null };
  const out: BasisLine[] = [];
  if (trip.cost > 0) out.push({ ...base, key: '', text: `Resekostnad ${where} (självkostnad)`, qty: 1, unit: 'once', unitPrice: Math.round(trip.cost * 100) / 100, itemKey: 'travel' });
  const rate = travelTimeRate(r, listHourly);
  if (trip.hours > 0 && rate > 0) out.push({ ...base, key: '', text: `Restid ${where}`, qty: Math.round(trip.hours * 100) / 100, unit: 'h', unitPrice: rate, itemKey: 'travel_time' });
  return out;
}

/** The rule in one sentence for the client card and the basis. */
export function travelSummary(r: TravelRule, listHourly: number, lang: 'sv' | 'en' = 'sv'): string {
  const rate = travelTimeRate(r, listHourly);
  const share = r.timeShare === 0.5 ? (lang === 'sv' ? 'halva timpriset' : 'half the hourly price') : `${Math.round(r.timeShare * 100)} %`;
  const time =
    r.timeShare === 0
      ? lang === 'sv'
        ? 'restiden ingår'
        : 'travel time is free'
      : lang === 'sv'
        ? `restid ${rate.toLocaleString('sv-SE')} kr/h (${share})`
        : `travel time ${rate.toLocaleString('en-GB')} kr/h (${share})`;
  if (r.included === 'all') return lang === 'sv' ? 'Alla resor ingår i priset.' : 'All travel is included in the price.';
  const where = r.included === 'none' ? (lang === 'sv' ? 'Alla resor, även inom Stockholm,' : 'All travel, Stockholm too, is') : lang === 'sv' ? 'Resor inom Stockholm ingår. Utanför' : 'Travel inside Stockholm is included. Outside, it is';
  return lang === 'sv' ? `${where} debiteras resekostnad + ${time}.` : `${where} invoiced: the cost + ${time}.`;
}

/** A deviating travel rule as a special term in the agreement; null for the standard (general terms 6.1 apply). */
export function contractTravelTerm(r: TravelRule | null | undefined, hourly: number): string | null {
  if (!r || isStandardTravel(r)) return null;
  const note = r.note ? ` ${r.note.replace(/\.?$/, '.')}` : '';
  if (r.included === 'all') return `Resor, logi och restid ingår i priset, oavsett var inspelningen sker. Det gäller i stället för Allmänna villkor 6.1.${note}`;
  const where = r.included === 'none' ? 'även inom Stockholm' : 'utanför Stockholm';
  const rate = travelTimeRate(r, hourly);
  const time = r.timeShare === 0 ? 'Restid ingår i priset' : `Restid debiteras med ${rate.toLocaleString('sv-SE')} kronor per timme exklusive moms`;
  return `Resor och logi ${where} debiteras enligt självkostnad. ${time}. Det gäller i stället för Allmänna villkor 6.1.${note}`;
}
